import { createFileRoute } from "@tanstack/react-router";
import { SiteHeaderEn } from "@/components/SiteHeaderEn";
import { SiteFooterEn } from "@/components/SiteFooterEn";
import { LeadSection } from "@/components/LeadSection";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Conversion FAQ, Scope, Pricing, Timeline";
const DESCRIPTION =
  "Answers on working with a freelance Shopify conversion specialist: scope, fixed fee, timelines, measurement, theme access and what happens after the sprint.";

const faqs = [
  {
    q: "How long does a conversion sprint take?",
    a: "Four to six weeks from kick-off to go-live: roughly one week of diagnosis, then implementation in agreed stages. Larger programmes run over three months.",
  },
  {
    q: "How do you measure the result?",
    a: "On the metrics we agree before the start: conversion rate overall and on mobile, average order value and revenue per session. Same period length, same traffic sources, before and after.",
  },
  {
    q: "Do you work with my existing theme?",
    a: "Yes. In most cases the fastest path to revenue is fixing the theme you have. A rebuild is only proposed when the current theme is the actual constraint.",
  },
  {
    q: "What do you need from me?",
    a: "Access to the store and analytics, and one person who can decide. That is usually enough; I do not need weekly workshops.",
  },
  {
    q: "Do you offer discounts?",
    a: "No. The price reflects the scope. If it is not the right time for you, come back when it is, the offer will still be there.",
  },
  {
    q: "Can you migrate from WooCommerce to Shopify?",
    a: "Yes. Products, variants, customers and orders move over, and the URL structure is preserved through redirects so rankings and existing links keep working.",
  },
  {
    q: "Do you work with clients outside Germany?",
    a: "Yes. Work happens remotely, in English or German, across Europe and beyond. Invoicing is in EUR, net.",
  },
  {
    q: "What if the numbers say it will not pay off?",
    a: "Then I say so before we start. A store with very little traffic or a structural margin problem needs something other than conversion work.",
  },
];

export const Route = createFileRoute("/en/faq")({
  component: FaqEn,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/en/faq` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/faq` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/faq" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/faq" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/faq" },
      { rel: "alternate", hrefLang: "de", href: `${SITE}/faq` },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/faq` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function FaqEn() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeaderEn />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">FAQ</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Questions before we <span className="text-signal">start</span>
            </h1>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-3xl gap-4 px-5">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-lg border border-border bg-card p-6">
                <h2 className="text-lg font-semibold">{f.q}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <LeadSection source="en-faq" lang="en" />
      </main>
      <SiteFooterEn />
    </div>
  );
}
